import type { StagecoachInferenceMode } from "@redotech/redo-model/coverage-calculator/coverage-ml/model/stagecoach";
import {
  CoverageMlModelId,
  INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
  STAGECOACH_COVERAGE_ML_MODEL_ID,
} from "@redotech/redo-model/coverage-calculator/coverage-ml/types";
import {
  AutomationStrategy,
  PackageProtectionSettings,
  PricingRuleSet,
  Team,
} from "@redotech/redo-model/team";
import {
  groupInput,
  Input,
  input,
  InputProvider,
  numberValidator,
} from "@redotech/ui/form";
import { arrayEqual, stringEqual } from "@redotech/util/equal";
import { nativeRandom, randomHexString } from "@redotech/util/random";
import round from "lodash/round";
import { memo } from "react";
import { RedoIncrementDecrement } from "../arbiter-components/increment-decrement/redo-increment-decrement";
import { Flex } from "../flex";
import { Text } from "../text";
import {
  CoverageMlModelMode,
  minMaxValidator,
  PackageProtectionMlModelSettings,
  Pricing,
  pricingDefault,
  pricingFormValues,
  pricingRulesConvertValueToTeam,
} from "./dynamic-pricing";

export const packageProtectionForm = groupInput(
  {
    // General
    enabled: input<boolean>(),
    customerClaimsEnabled: input<boolean>(),
    automationStrategy: input<AutomationStrategy>(),
    timeTillApprovalHours: input<string>({
      validator: numberValidator({ min: 1 }),
    }),
    packageProtectionPlusEnabled: input<boolean>(),
    packageProtectionPlusPercentage: input<string>(),
    packageProtectionPlusAllOrdersCovered: input<boolean>(),
    packageProtectionPlusAllClaimsCovered: input<boolean>(),
    packageProtectionPlusShippingIncluded: input<boolean>(),
    newClaimsTables: input<boolean>(),
    // Coverage
    coverage: input<boolean>(),
    // Exclusions
    protectionExcludeTags: input<string[]>({ equal: arrayEqual(stringEqual) }),
    minCoverageEligibleCartTotal: input<number | undefined>({
      validator: (value) => {
        if (value === undefined) {
          return [];
        }
        if (value < 0) {
          return ["Cart eligibility threshold must be a positive number"];
        }
        return [];
      },
    }),
    maxCoverageEligibleCartTotal: input<number | undefined>({
      validator: (value) => {
        if (value === undefined) {
          return [];
        }
        if (value <= 0) {
          return ["Cart eligibility threshold must be a positive number"];
        }
        return [];
      },
    }),
    fixedSubscriptionPrice: input<number | undefined>({
      validator: (value) => {
        if (value === undefined) {
          return [];
        }
        if (value < 0) {
          return ["Fixed subscription price must be a positive number"];
        }
        return [];
      },
    }),
    // Pricing
    ...pricingFormValues,
    // ML Model
    coverageMlModelEnabled: input<boolean>(),
    coverageMlModelId: input<CoverageMlModelId>(),
    intentModelConfig: groupInput<{
      minPriceBiasPercentOfConstraintRange: Input<string>;
      maxPriceBiasPercentOfConstraintRange: Input<string>;
    }>({
      minPriceBiasPercentOfConstraintRange: input<string>({
        validator: numberValidator({ min: -1, max: 1 }),
      }),
      maxPriceBiasPercentOfConstraintRange: input<string>({
        validator: numberValidator({ min: -1, max: 1 }),
      }),
    }),
    stagecoachPriceVsConversionWeight: input<string>({
      validator: numberValidator({ min: 0 }),
    }),
    stagecoachThompsonSamplingEnabled: input<boolean>(),
    stagecoachInferenceMode: input<StagecoachInferenceMode | undefined>(),
  },
  { validator: minMaxValidator },
);

export type PackageProtectionForm = InputProvider.Form<
  typeof packageProtectionForm
>;

export type PackageProtectionValue = InputProvider.Value<
  typeof packageProtectionForm
>;

export const packageProtectionDefault: PackageProtectionValue = {
  // General
  enabled: false,
  customerClaimsEnabled: false,
  automationStrategy: AutomationStrategy.AUTO_REJECT_APPROVE,
  timeTillApprovalHours: "24",
  packageProtectionPlusEnabled: false,
  packageProtectionPlusPercentage: "0",
  packageProtectionPlusAllOrdersCovered: false,
  packageProtectionPlusAllClaimsCovered: false,
  packageProtectionPlusShippingIncluded: true,
  newClaimsTables: true,
  // Coverage
  coverage: false,
  // Exclusions
  protectionExcludeTags: [],
  minCoverageEligibleCartTotal: undefined,
  maxCoverageEligibleCartTotal: undefined,
  fixedSubscriptionPrice: undefined,
  // Pricing
  ...pricingDefault,
  // ML Model
  coverageMlModelEnabled: false,
  coverageMlModelId: INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
  intentModelConfig: {
    minPriceBiasPercentOfConstraintRange: "",
    maxPriceBiasPercentOfConstraintRange: "",
  },
  stagecoachPriceVsConversionWeight: "1.0",
  stagecoachThompsonSamplingEnabled: false,
  stagecoachInferenceMode: undefined,
};

export const PackageProtectionPricing = memo(function PackageProtectionPricing({
  input,
  canEditSettings,
  coverageMlModelMode,
  adminApp = false,
  currencyDisplay,
  email,
}: {
  input: PackageProtectionForm;
  canEditSettings: boolean;
  coverageMlModelMode: CoverageMlModelMode;
  adminApp?: boolean;
  currencyDisplay?: string;
  email?: string;
}) {
  return (
    <>
      <PackageProtectionMlModelSettings
        canEditSettings={canEditSettings}
        coverageMlModelEnabled={input.inputs.coverageMlModelEnabled}
        coverageMlModelId={input.inputs.coverageMlModelId}
        coverageMlModelMode={coverageMlModelMode}
        currencyDisplay={currencyDisplay}
        email={email}
        intentModelConfig={input.inputs.intentModelConfig}
        maxPriceInput={input.inputs.maxPrice}
        minPriceInput={input.inputs.minPrice}
        stagecoachPriceVsConversionWeight={
          input.inputs.stagecoachPriceVsConversionWeight
        }
        stagecoachThompsonSamplingEnabled={
          input.inputs.stagecoachThompsonSamplingEnabled
        }
      />
      {(!input.inputs.coverageMlModelEnabled.value ||
        input.inputs.coverageMlModelId.value ===
          INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID) && (
        <Pricing
          adminApp={adminApp}
          canEditSettings={canEditSettings}
          currencyDisplay={currencyDisplay}
          input={input}
        />
      )}
      <Flex dir="column" gap="xs" w="full">
        <Text fontSize="md">Cart total boundaries</Text>
        <Flex dir="row" flex="1" gap="md">
          <Flex flex={1}>
            <RedoIncrementDecrement
              disabled={!canEditSettings}
              error={
                input.inputs.minCoverageEligibleCartTotal.errors.length > 0
              }
              fullWidth
              min={0}
              placeholder="No minimum"
              prefix={
                typeof input.value.minCoverageEligibleCartTotal === "number"
                  ? "$"
                  : ""
              }
              selectOnFocus
              setValue={input.inputs.minCoverageEligibleCartTotal.setValue}
              size="regular"
              step="any"
              value={input.value.minCoverageEligibleCartTotal}
            />
          </Flex>
          <Flex flex={1}>
            <RedoIncrementDecrement
              disabled={!canEditSettings}
              error={
                input.inputs.maxCoverageEligibleCartTotal.errors.length > 0
              }
              fullWidth
              min={0}
              placeholder="No maximum"
              prefix={
                typeof input.value.maxCoverageEligibleCartTotal === "number"
                  ? "$"
                  : ""
              }
              selectOnFocus
              setValue={input.inputs.maxCoverageEligibleCartTotal.setValue}
              size="regular"
              step="any"
              value={input.value.maxCoverageEligibleCartTotal}
            />
          </Flex>
        </Flex>
        <Text fontSize="sm">
          Package protection will not be offered if the cart total is not within
          these boundaries
        </Text>
      </Flex>
      <Flex dir="column" gap="xs" pt="lg" w="full">
        <Text fontSize="md">Fixed subscription price</Text>
        <RedoIncrementDecrement
          disabled={!canEditSettings}
          error={input.inputs.fixedSubscriptionPrice.errors.length > 0}
          fullWidth
          min={0}
          placeholder="No fixed price"
          prefix={
            typeof input.value.fixedSubscriptionPrice === "number" ? "$" : ""
          }
          selectOnFocus
          setValue={input.inputs.fixedSubscriptionPrice.setValue}
          size="regular"
          step="any"
          value={input.value.fixedSubscriptionPrice}
        />
        <Text fontSize="sm">
          Set a fixed price for package protection when there are subscription
          items in the cart
        </Text>
      </Flex>
    </>
  );
});

export function pricingRulesConvertTeamToValue(team: Team) {
  let pricingRuleSetReturn: any = [];
  if (team.settings.packageProtection?.pricingRuleSet?.length) {
    pricingRuleSetReturn = team.settings.packageProtection?.pricingRuleSet.map(
      (x: PricingRuleSet) => {
        return {
          type: x.type,
          countries: x.countries,
          key: randomHexString(nativeRandom, 12),
          priceBrackets: x.priceBrackets.map((pb) => {
            let value = pb.value;
            if (x.type === "percentage") {
              value = round(pb.value * 100, 3);
            }
            return {
              key: randomHexString(nativeRandom, 12),
              value: String(value),
              pricePoint: String(pb.pricePoint),
            };
          }),
        };
      },
    );
  } else if (!pricingRuleSetReturn.length) {
    pricingRuleSetReturn.push({
      key: randomHexString(nativeRandom, 12),
      type: "percentage",
      countries: team.settings.countriesPackageProtection,
      priceBrackets: [
        {
          key: randomHexString(nativeRandom, 12),
          value: team.settings.packageProtection?.percentage
            ? String(
                round(team.settings.packageProtection?.percentage * 100, 3),
              )
            : "2",
          pricePoint: "0",
        },
      ],
    });
  }

  return pricingRuleSetReturn;
}

export function packageProtectionTeamToFormValue(
  team: Team,
): PackageProtectionValue {
  return {
    // General
    enabled: team.settings.packageProtection?.enabled || false,
    customerClaimsEnabled:
      team.settings.packageProtection?.customerClaimsEnabled || false,
    automationStrategy:
      team.settings.packageProtection?.automationStrategy?.strategy ||
      AutomationStrategy.TIME_BASED_APPROVAL,
    timeTillApprovalHours: team.settings.packageProtection?.automationStrategy
      ?.options?.timeTillApprovalMs
      ? Temporal.Duration.from({
          milliseconds:
            team.settings.packageProtection?.automationStrategy?.options
              ?.timeTillApprovalMs,
        })
          .total({ unit: "hours" })
          .toString()
      : "24",
    packageProtectionPlusEnabled:
      team.settings.packageProtection?.packageProtectionPlusEnabled || false,
    packageProtectionPlusPercentage: team.settings.packageProtection
      ?.packageProtectionPlusPercentage
      ? String(
          round(
            team.settings.packageProtection.packageProtectionPlusPercentage *
              100.0,
            3,
          ),
        )
      : "0",
    packageProtectionPlusAllOrdersCovered:
      team.settings.packageProtection?.packageProtectionPlusAllOrdersCovered ||
      false,
    packageProtectionPlusAllClaimsCovered:
      team.settings.packageProtection?.packageProtectionPlusAllClaimsCovered ||
      false,
    packageProtectionPlusShippingIncluded:
      team.settings.packageProtection?.packageProtectionPlusShippingIncluded ||
      true,
    newClaimsTables: team.settings.packageProtection?.newClaimsTables ?? true,
    // Coverage
    coverage: team.settings.packageProtection?.coverage || false,
    // Exclusions
    protectionExcludeTags: team.settings.protectionExcludeTags || [],
    minPrice: team.settings.packageProtection?.minPrice?.toString() || "1",
    maxPrice: team.settings.packageProtection?.maxPrice?.toString() || "15",
    percentage: team.settings.packageProtection?.percentage
      ? String(round(team.settings.packageProtection.percentage * 100.0, 3))
      : "2",
    pricingRuleSet: pricingRulesConvertTeamToValue(team),
    internationalPricingEnabled: team.settings.packageProtection?.pricingRuleSet
      ?.length
      ? team.settings.packageProtection?.pricingRuleSet?.length > 1
      : false,
    minCoverageEligibleCartTotal:
      team.settings.packageProtection?.minCoverageEligibleCartTotal,
    maxCoverageEligibleCartTotal:
      team.settings.packageProtection?.maxCoverageEligibleCartTotal,
    fixedSubscriptionPrice:
      team.settings.packageProtection?.fixedSubscriptionPrice,
    coverageMlModelEnabled: !!team.settings.packageProtection?.coverageMlModel,
    coverageMlModelId:
      team.settings.packageProtection?.coverageMlModel?.id ||
      INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
    intentModelConfig: {
      minPriceBiasPercentOfConstraintRange:
        team.settings.packageProtection?.coverageMlModel?.id ===
        INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID
          ? team.settings.packageProtection?.coverageMlModel?.config?.minPriceBiasPercentOfConstraintRange?.toString() ||
            ""
          : "",
      maxPriceBiasPercentOfConstraintRange:
        team.settings.packageProtection?.coverageMlModel?.id ===
        INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID
          ? team.settings.packageProtection?.coverageMlModel?.config?.maxPriceBiasPercentOfConstraintRange?.toString() ||
            ""
          : "",
    },
    stagecoachPriceVsConversionWeight:
      team.settings.packageProtection?.coverageMlModel?.id ===
      STAGECOACH_COVERAGE_ML_MODEL_ID
        ? team.settings.packageProtection?.coverageMlModel?.config?.priceVsConversionWeight?.toString() ||
          "1.0"
        : "1.0",
    stagecoachThompsonSamplingEnabled:
      team.settings.packageProtection?.coverageMlModel?.id ===
      STAGECOACH_COVERAGE_ML_MODEL_ID
        ? (team.settings.packageProtection?.coverageMlModel?.config
            ?.thompsonSamplingEnabled ?? false)
        : false,
    stagecoachInferenceMode:
      team.settings.packageProtection?.coverageMlModel?.id ===
      STAGECOACH_COVERAGE_ML_MODEL_ID
        ? (team.settings.packageProtection?.coverageMlModel?.config
            ?.inferenceMode ?? undefined)
        : undefined,
  };
}

export function packageProtectionFormValueToTeam(
  packageProtection: PackageProtectionValue,
  splitProducts: boolean,
): PackageProtectionSettings {
  return {
    enabled: packageProtection.enabled,
    coverage: packageProtection.coverage,
    customerClaimsEnabled: packageProtection.customerClaimsEnabled,
    automationStrategy: {
      strategy: packageProtection.automationStrategy,
      options: {
        timeTillApprovalMs: Temporal.Duration.from({
          hours: Number(packageProtection.timeTillApprovalHours),
        }).total({ unit: "milliseconds" }),
      },
    },
    packageProtectionPlusEnabled:
      packageProtection.packageProtectionPlusEnabled,
    packageProtectionPlusAllOrdersCovered:
      packageProtection.packageProtectionPlusAllOrdersCovered,
    packageProtectionPlusAllClaimsCovered:
      packageProtection.packageProtectionPlusAllClaimsCovered,
    packageProtectionPlusPercentage:
      round(+packageProtection.packageProtectionPlusPercentage, 3) / 100.0,
    packageProtectionPlusShippingIncluded:
      packageProtection.packageProtectionPlusShippingIncluded,
    newClaimsTables: packageProtection.newClaimsTables,
    minPrice: Number(packageProtection.minPrice),
    maxPrice: Number(packageProtection.maxPrice),
    percentage: round(+packageProtection.percentage, 3) / 100.0,
    splitProducts,
    pricingRuleSet: pricingRulesConvertValueToTeam(
      packageProtection.pricingRuleSet,
    ),
    minCoverageEligibleCartTotal: packageProtection.minCoverageEligibleCartTotal
      ? packageProtection.minCoverageEligibleCartTotal
      : undefined,
    maxCoverageEligibleCartTotal: packageProtection.maxCoverageEligibleCartTotal
      ? packageProtection.maxCoverageEligibleCartTotal
      : undefined,
    fixedSubscriptionPrice: packageProtection.fixedSubscriptionPrice
      ? packageProtection.fixedSubscriptionPrice
      : undefined,
    coverageMlModel:
      packageProtection.coverageMlModelEnabled &&
      packageProtection.coverageMlModelId
        ? packageProtection.coverageMlModelId ===
          INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID
          ? {
              id: packageProtection.coverageMlModelId,
              config: {
                minPriceBiasPercentOfConstraintRange: packageProtection
                  .intentModelConfig.minPriceBiasPercentOfConstraintRange
                  ? parseFloat(
                      packageProtection.intentModelConfig
                        .minPriceBiasPercentOfConstraintRange,
                    )
                  : undefined,
                maxPriceBiasPercentOfConstraintRange: packageProtection
                  .intentModelConfig.maxPriceBiasPercentOfConstraintRange
                  ? parseFloat(
                      packageProtection.intentModelConfig
                        .maxPriceBiasPercentOfConstraintRange,
                    )
                  : undefined,
              },
            }
          : packageProtection.coverageMlModelId ===
              STAGECOACH_COVERAGE_ML_MODEL_ID
            ? {
                id: packageProtection.coverageMlModelId,
                config: {
                  priceVsConversionWeight:
                    packageProtection.stagecoachPriceVsConversionWeight
                      ? parseFloat(
                          packageProtection.stagecoachPriceVsConversionWeight,
                        )
                      : undefined,
                  thompsonSamplingEnabled:
                    packageProtection.stagecoachThompsonSamplingEnabled,
                  inferenceMode: packageProtection.stagecoachInferenceMode,
                },
              }
            : { id: packageProtection.coverageMlModelId }
        : undefined,
  };
}
